import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import VipSurveyView from '../components/vip-surveys/VipSurveyView';
import { vipSurveys } from '../services/vip-surveys';
import type { VipResponse, VipRound } from '../types/vip-surveys';
import { buildLoginUrl, currentPathForNext } from '../utils/redirectUtils';

function errorMessage(error: any, fallback: string): string {
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  return fallback;
}

function hasAnswer(value: unknown): boolean {
  return value !== undefined && (typeof value !== 'string' || value.trim().length > 0);
}

const VipSurvey: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id;
  const [round, setRound] = useState<VipRound | null>(null);
  const [response, setResponse] = useState<VipResponse | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | number | boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [retry, setRetry] = useState(0);
  const submitting = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    if (authLoading) return;
    const requestGeneration = ++generation.current;
    let cancelled = false;
    setLoading(true);
    setRound(null);
    setResponse(null);
    setAnswers({});
    setAttempted(false);
    setError('');
    submitting.current = false;
    setSaving(false);
    (async () => {
      try {
        const [nextRound, existing] = await Promise.all([
          vipSurveys.getRound(code),
          userId ? vipSurveys.getMyResponse(code) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setRound(nextRound);
        setResponse(existing);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'دریافت پرسشنامه ممکن نشد. لطفاً دوباره تلاش کنید.'));
      } finally {
        if (!cancelled && requestGeneration === generation.current) setLoading(false);
      }
    })();
    return () => { cancelled = true; generation.current += 1; };
  }, [code, userId, authLoading, retry]);

  const questions = round?.questionnaire.questions ?? [];
  const missing = questions.filter((q) => q.required && !hasAnswer(answers[q.id]));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || !round || !userId || response) return;
    setAttempted(true);
    setError('');
    if (missing.length) {
      document.getElementById(`vip-question-${missing[0].id}`)?.focus();
      setError('لطفاً به سؤال‌های الزامی پاسخ دهید.');
      return;
    }
    submitting.current = true;
    setSaving(true);
    const requestGeneration = generation.current;
    try {
      const result = await vipSurveys.submit(code, questions
        .filter((q) => hasAnswer(answers[q.id]))
        .map((q) => ({ questionId: q.id, value: typeof answers[q.id] === 'string' ? String(answers[q.id]).trim() : answers[q.id] })));
      if (requestGeneration === generation.current) setResponse(result);
    } catch (err: any) {
      if (requestGeneration !== generation.current) return;
      if (err?.response?.status === 409) {
        try {
          const existing = await vipSurveys.getMyResponse(code);
          if (requestGeneration === generation.current && existing) {
            setResponse(existing);
            return;
          }
        } catch (_) { /* Keep the form available if recovery fails. */ }
      }
      if (requestGeneration === generation.current) setError(errorMessage(err, 'ثبت پاسخ‌ها انجام نشد. پاسخ‌های شما در فرم باقی مانده؛ دوباره تلاش کنید.'));
    } finally {
      if (requestGeneration === generation.current) {
        submitting.current = false;
        setSaving(false);
      }
    }
  };

  const setAnswer = (id: string, value: string | number | boolean) => {
    setAnswers((previous) => ({ ...previous, [id]: value }));
    setError('');
  };

  return <VipSurveyView
    loading={loading || authLoading}
    round={round}
    response={response}
    userName={user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.phone || user.username : null}
    answers={answers}
    answeredIds={questions.filter(question => hasAnswer(answers[question.id])).map(question => question.id)}
    missingIds={missing.map(question => question.id)}
    attempted={attempted}
    saving={saving}
    error={error}
    loginUrl={buildLoginUrl(currentPathForNext(location))}
    onAnswer={setAnswer}
    onClear={id => { setAnswers(previous => { const next = { ...previous }; delete next[id]; return next; }); setError(''); }}
    onRetry={() => setRetry(previous => previous + 1)}
    onSubmit={submit}
  />;
};

export default VipSurvey;
